import type {
  IAvailableOffer,
  IPlanChangeEligibility,
} from 'services/plan/types';

export const PLAN_TIER_ORDER: Record<string, number> = {
  ultimate: 6,
  premier: 5,
  complete: 4,
  total: 3,
  'total plus': 3,
  value: 2,
  'value essential': 2,
  basic: 1,
};

function normType(t: string): string {
  return t.toLowerCase();
}

/**
 * Billing terms are binary across this flow — ANNUAL, or everything else is
 * MONTHLY. Bucketing both sides through this is what makes two term strings
 * safely comparable, whatever casing (or absence) the server sends.
 */
export function normalizeUserBillingTerm(
  term?: string | null,
): 'MONTHLY' | 'ANNUAL' {
  return term?.toUpperCase() === 'ANNUAL' ? 'ANNUAL' : 'MONTHLY';
}

/** Upsell flows (Offers & Upgrades) use upgrade copy; default/custom use CTP convert copy. */
export function isCtpUpsellOfferChangeType(type: string | string[]): boolean {
  const types = Array.isArray(type) ? type : [type];

  return types.some((t) => {
    const normalized = t.toLowerCase();
    return normalized === 'ctp upsell' || normalized === 'ctp upsell family';
  });
}

export function filterValidMarketingOffers(
  offers: IAvailableOffer[],
  eligibility: IPlanChangeEligibility | null,
): IAvailableOffer[] {
  const prods = offers.filter((product) => {
    const cfg = product.offerChangeConfiguration;
    if (!product.offer || !product.plan || !cfg) return false;
    if (!cfg.toOfferCode) return false;
    const allowed = cfg.allowedSources ?? [];
    return allowed.some((s) => s.toLowerCase() === 'primary');
  });

  const isEligible = eligibility?.eligible === true;
  const reasonCode = eligibility?.reasonCode ?? '';

  let offerTypes: string[];
  if (isEligible) {
    offerTypes = ['ctp upsell family', 'custom', 'ctp upsell'];
  } else if (
    reasonCode === 'NOT_WITHIN_ELIGIBILITY_PERIOD' ||
    reasonCode === 'BREACH_COMPLIMENTARY_LIMITED_OFFER_CHANGE_SCHEDULED'
  ) {
    offerTypes = ['ctp upsell family', 'ctp upsell'];
  } else {
    offerTypes = ['ctp upsell family', 'ctp upsell'];
  }

  let finalProds = prods.filter((product) => {
    const productType = normType(product.offerChangeConfiguration.type);
    return offerTypes.some((ot) => productType === normType(ot));
  });

  if (finalProds.length === 0) {
    finalProds = prods.filter(
      (product) => product.offerChangeConfiguration.type === 'default',
    );
  }

  return finalProds.filter((p) => p.offerChangeConfiguration.toOfferCode);
}

export function tierRank(tier: string | undefined): number {
  if (!tier) return 0;
  return PLAN_TIER_ORDER[tier.toLowerCase()] ?? 0;
}

export function filterUpgradeOffersForFamilyMode(
  validOffers: IAvailableOffer[],
  opts: {
    wantFamily: boolean;
    currentPlanCode: string | undefined;
    currentBillingType: string | null | undefined;
    currentPrice: number;
  },
): IAvailableOffer[] {
  const { wantFamily, currentPlanCode, currentBillingType, currentPrice } =
    opts;

  return validOffers.filter((product: IAvailableOffer) => {
    const isFamilyPlan = product.plan.family;
    if (wantFamily !== isFamilyPlan) return false;
    const hasHigherPrice = product.offer.price >= currentPrice;
    const samePlan =
      product.plan.code === currentPlanCode &&
      product.offer.billingType === currentBillingType;
    return hasHigherPrice && !samePlan;
  });
}

export function sortOffersByTierDesc(
  offers: IAvailableOffer[],
): IAvailableOffer[] {
  return [...offers].sort((a, b) => {
    const aTier = tierRank(a.plan.tier);
    const bTier = tierRank(b.plan.tier);
    if (bTier !== aTier) return bTier - aTier;
    return a.plan.code.localeCompare(b.plan.code);
  });
}

export function uniqueTermNamesFromOffers(offers: IAvailableOffer[]): string[] {
  const terms = new Set<string>();
  offers.forEach((o) => {
    const tn = o.offer.subscriptionPlan?.termName;
    if (tn) terms.add(tn.toUpperCase());
  });
  return [...terms];
}

export function mergeBillingTermSelectorOptions(
  upgradeTerms: string[],
  subscriptionTerm: string,
): string[] {
  const terms = new Set<string>();
  upgradeTerms.forEach((t) => terms.add(t.toUpperCase()));
  terms.add(subscriptionTerm.toUpperCase());

  const preferredOrder = ['MONTHLY', 'ANNUAL'];
  const ordered: string[] = [];
  preferredOrder.forEach((key) => {
    if (terms.has(key)) {
      ordered.push(key);
      terms.delete(key);
    }
  });
  [...terms].sort().forEach((t) => ordered.push(t));
  return ordered;
}

export function augmentPlanFromAvailableOffers(
  offers: IAvailableOffer[],
  offerCode: string | null | undefined,
  planCode: string | null | undefined,
): { tier: string; features: string[] } {
  let tier = '';
  let features: string[] = [];

  const matchingOffers = offerCode
    ? offers.filter((o) => o.offer.code === offerCode)
    : [];

  const byOffer =
    planCode && matchingOffers.length > 0
      ? (matchingOffers.find((o) => o.plan.code === planCode) ??
        matchingOffers[0])
      : matchingOffers[0];

  if (byOffer) {
    tier = byOffer.plan.tier?.trim() ?? '';
    if (byOffer.plan.features?.length) {
      features = [...byOffer.plan.features];
    }
  }

  if (planCode) {
    const byPlan = offers.find((o) => o.plan.code === planCode);
    if (byPlan) {
      if (!tier) tier = byPlan.plan.tier?.trim() ?? '';
      if (!features.length && byPlan.plan.features?.length) {
        features = [...byPlan.plan.features];
      }
    }
  }

  return { tier, features };
}
